import React, { useMemo } from 'react';

export interface IPSimplePaginationProps {
  /** 1-based aktif sayfa */
  currentPage: number;
  /** Toplam sayfa sayısı (en az 1) */
  pageCount: number;
  /** Kullanıcı bir sayfa seçtiğinde (1-based) */
  onPageChange: (page: number) => void;
  /** Aktif sayfa li sınıfı */
  activeClassName?: string;
  containerClassName?: string;
  pageClassName?: string;
  pageLinkClassName?: string;
  breakClassName?: string;
  breakLabel?: React.ReactNode;
  /** Mevcut sayfanın her iki yanında kaç sayfa numarası gösterileceği (react-paginate `pageRangeDisplayed` ile uyumlu his) */
  siblingCount?: number;
  previousLabel?: React.ReactNode;
  nextLabel?: React.ReactNode;
}

/**
 * react-paginate bağımlılığı olmadan Bootstrap `pagination` yapısına uyumlu,
 * tamamen kontrollü (controlled) sayfalama.
 */
function buildPageItems(currentPage: number, pageCount: number, delta: number): Array<number | 'ellipsis'> {
  if (pageCount < 1) return [];
  if (pageCount === 1) return [1];

  const range: number[] = [];
  for (let i = 1; i <= pageCount; i++) {
    if (i === 1 || i === pageCount || (i >= currentPage - delta && i <= currentPage + delta)) {
      range.push(i);
    }
  }

  const withDots: Array<number | 'ellipsis'> = [];
  let prev: number | undefined;
  for (const i of range) {
    if (prev !== undefined) {
      if (i - prev === 2) {
        withDots.push(prev + 1);
      } else if (i - prev !== 1) {
        withDots.push('ellipsis');
      }
    }
    withDots.push(i);
    prev = i;
  }
  return withDots;
}

const IPSimplePagination: React.FC<IPSimplePaginationProps> = ({
  currentPage,
  pageCount,
  onPageChange,
  activeClassName = 'active',
  containerClassName = 'pagination',
  pageClassName = 'page-item',
  pageLinkClassName = 'page-link btn btn-icon btn-sm btn-light mr-2 my-1',
  breakClassName = 'page-link mr-2 my-1 mx-2',
  breakLabel = '...',
  siblingCount = 2,
  previousLabel,
  nextLabel,
}) => {
  const { safePage, items } = useMemo(() => {
    if (pageCount < 1) {
      return { safePage: 1, items: [] as Array<number | 'ellipsis'> };
    }
    const sp = Math.min(Math.max(1, currentPage), pageCount);
    return { safePage: sp, items: buildPageItems(sp, pageCount, siblingCount) };
  }, [currentPage, pageCount, siblingCount]);

  if (pageCount < 1) {
    return null;
  }

  const defaultPrevious = (
    <div className="page-item previous mx-2">
      <button type="button" disabled={safePage <= 1} className="btn btn-icon btn-sm btn-light mr-2 my-1 page-link" onClick={() => safePage > 1 && onPageChange(safePage - 1)}>
        <i className="previous"></i>
      </button>
    </div>
  );

  const defaultNext = (
    <div className="page-item next mx-2">
      <button
        type="button"
        disabled={safePage >= pageCount}
        className="btn btn-icon btn-sm btn-light mr-2 my-1 page-link"
        onClick={() => safePage < pageCount && onPageChange(safePage + 1)}
      >
        <i className="next"></i>
      </button>
    </div>
  );

  return (
    <ul className={containerClassName}>
      <li className={`${pageClassName} d-inline-flex align-items-center`}>{previousLabel ?? defaultPrevious}</li>
      {items.map((item, idx) =>
        item === 'ellipsis' ? (
          <li key={`ellipsis-${idx}`} className={pageClassName}>
            <span className={breakClassName} aria-hidden>
              {breakLabel}
            </span>
          </li>
        ) : (
          <li key={item} className={`${pageClassName} ${item === safePage ? activeClassName : ''}`}>
            <button type="button" className={pageLinkClassName} aria-current={item === safePage ? 'page' : undefined} onClick={() => item !== safePage && onPageChange(item)}>
              {item}
            </button>
          </li>
        ),
      )}
      <li className={`${pageClassName} d-inline-flex align-items-center`}>{nextLabel ?? defaultNext}</li>
    </ul>
  );
};

export default IPSimplePagination;
